import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownAZ,
  ArrowUpDown,
  Building2,
  Calendar,
  Check,
  FileText,
  Filter,
  Loader2,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useQuotes, QuoteSortField, SortDir } from '../hooks/useQuotes';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MoveCard } from '../components/ui/MoveCard';
import { AssignEstimatorSheet } from '../components/ui/AssignEstimatorSheet';
import { NewMoveForm, type NewMoveFormData } from '../components/moves/NewMoveForm';
import { api } from '../lib/api';
import {
  Quote,
  QuoteStatus,
  QuoteType,
  QUOTE_STATUS_CLASSES,
  QUOTE_STATUS_LABEL,
} from '../types/admin';

// ── All quote statuses ────────────────────────────────────────────────────────

const ALL_STATUSES: QuoteStatus[] = ['draft', 'sent', 'response_received', 'booked', 'declined', 'cancelled'];

// ── Sort options ──────────────────────────────────────────────────────────────

interface SortOption {
  field: QuoteSortField;
  dir: SortDir;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { field: 'estimateVisitDate', dir: 'asc', label: 'Estimate date (soonest)' },
  { field: 'estimateVisitDate', dir: 'desc', label: 'Estimate date (latest)' },
  { field: 'createdAt', dir: 'desc', label: 'Newest first' },
  { field: 'createdAt', dir: 'asc', label: 'Oldest first' },
  { field: 'customerCompanyName', dir: 'asc', label: 'Company A–Z' },
  { field: 'customerCompanyName', dir: 'desc', label: 'Company Z–A' },
  { field: 'movingDate', dir: 'asc', label: 'Move date (soonest)' },
  { field: 'movingDate', dir: 'desc', label: 'Move date (latest)' },
  { field: 'quoteStatus', dir: 'asc', label: 'Status A–Z' },
];

// ── Filter Sheet ──────────────────────────────────────────────────────────────

interface FilterState {
  visibleStatuses: Set<QuoteStatus>;
  myQuotesOnly: boolean;
  typeCommercial: boolean;
  typeResidential: boolean;
}

interface FilterSheetProps {
  filters: FilterState;
  onApply: (f: FilterState) => void;
  onClose: () => void;
  showTypeFilter: boolean;
}

function FilterSheet({ filters, onApply, onClose, showTypeFilter }: FilterSheetProps) {
  const [visibleStatuses, setVisibleStatuses] = useState<Set<QuoteStatus>>(
    new Set(filters.visibleStatuses),
  );
  const [myQuotesOnly, setMyQuotesOnly] = useState(filters.myQuotesOnly);
  const [typeCommercial, setTypeCommercial] = useState(filters.typeCommercial);
  const [typeResidential, setTypeResidential] = useState(filters.typeResidential);

  function toggleStatus(s: QuoteStatus) {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  function handleApply() {
    onApply({ visibleStatuses, myQuotesOnly, typeCommercial, typeResidential });
    onClose();
  }

  function handleReset() {
    onApply({ visibleStatuses: new Set(ALL_STATUSES), myQuotesOnly: false, typeCommercial: true, typeResidential: true });
    onClose();
  }

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
      ${checked ? 'border-plum bg-plum' : 'border-stone'}`}>
      {checked && (
        <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2 text-white">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  return (
    /* Mobile: bottom sheet. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Filter Quotes</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Type filter — at the very top */}
        {showTypeFilter && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Type</p>
            <div className="flex flex-col gap-2 mb-5">
              <button
                type="button"
                onClick={() => setTypeCommercial(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${typeCommercial ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}
              >
                <CheckboxIcon checked={typeCommercial} />
                <span className={`text-small font-medium ${typeCommercial ? 'text-plum' : 'text-midnight'}`}>
                  Commercial
                </span>
              </button>
              <button
                type="button"
                onClick={() => setTypeResidential(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${typeResidential ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}
              >
                <CheckboxIcon checked={typeResidential} />
                <span className={`text-small font-medium ${typeResidential ? 'text-plum' : 'text-midnight'}`}>
                  Residential
                </span>
              </button>
            </div>
          </>
        )}

        {/* Status filters */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Status</p>
        <div className="flex flex-col gap-2 mb-5">
          {ALL_STATUSES.map(s => {
            const checked = visibleStatuses.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${checked
                    ? 'border-plum bg-plum/5'
                    : 'border-stone bg-white'
                  }`}
              >
                <CheckboxIcon checked={checked} />
                <span className={`text-small font-medium ${checked ? 'text-plum' : 'text-midnight'}`}>
                  {QUOTE_STATUS_LABEL[s]}
                </span>
              </button>
            );
          })}
        </div>

        {/* My Quotes toggle */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Scope</p>
        <button
          type="button"
          onClick={() => setMyQuotesOnly(v => !v)}
          className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left mb-5
            transition-all duration-150
            ${myQuotesOnly ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}
        >
          <CheckboxIcon checked={myQuotesOnly} />
          <div>
            <p className={`text-small font-medium ${myQuotesOnly ? 'text-plum' : 'text-midnight'}`}>
              My Quotes only
            </p>
            <p className="text-[11px] text-slate">Show only quotes you created</p>
          </div>
        </button>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 h-12 rounded-xl border border-stone text-small font-semibold text-slate
                       hover:border-plum/40 hover:text-midnight active:bg-mist
                       transition-colors duration-150"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sort Sheet ────────────────────────────────────────────────────────────────

interface SortSheetProps {
  sortBy: QuoteSortField;
  sortDir: SortDir;
  onApply: (field: QuoteSortField, dir: SortDir) => void;
  onClose: () => void;
}

function SortSheet({ sortBy, sortDir, onApply, onClose }: SortSheetProps) {
  const icons: Record<string, typeof ArrowDownAZ> = {
    createdAt: Calendar,
    customerCompanyName: Building2,
    movingDate: Calendar,
    status: ArrowDownAZ,
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Sort Quotes</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {SORT_OPTIONS.map(opt => {
            const active = sortBy === opt.field && sortDir === opt.dir;
            const Icon = icons[opt.field] ?? ArrowUpDown;
            return (
              <button
                key={`${opt.field}-${opt.dir}`}
                type="button"
                onClick={() => { onApply(opt.field, opt.dir); onClose(); }}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${active
                    ? 'border-plum bg-plum/5'
                    : 'border-stone bg-white hover:border-plum/40'
                  }`}
              >
                <Icon size={14} className={active ? 'text-plum' : 'text-slate'} />
                <span className={`text-small font-medium flex-1 ${active ? 'text-plum' : 'text-midnight'}`}>
                  {opt.label}
                </span>
                {active && <Check size={14} className="text-plum shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Stats Dashboard ───────────────────────────────────────────────────────────

interface StatsDashboardProps {
  draftCount: number;
  sentCount: number;
  respondedCount: number;
  activeStatus: QuoteStatus | null;
  onClickStatus: (status: QuoteStatus | null) => void;
  vertical?: boolean;
}

function StatsDashboard({ draftCount, sentCount, respondedCount, activeStatus, onClickStatus, vertical }: StatsDashboardProps) {
  return (
    <div className={`${vertical ? 'flex flex-col gap-2' : 'flex gap-2 mb-4'}`}>
      {/* Draft */}
      <button
        type="button"
        onClick={() => onClickStatus(activeStatus === 'draft' ? null : 'draft')}
        className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
          ${activeStatus === 'draft'
            ? 'border-plum bg-plum/5 shadow-brand-sm'
            : 'border-stone bg-white hover:border-plum/30'
          }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
          ${activeStatus === 'draft' ? 'bg-plum/15' : 'bg-stone/60'}`}>
          <FileText size={14} className={activeStatus === 'draft' ? 'text-plum' : 'text-slate'} />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{draftCount}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">Drafts</p>
        </div>
      </button>

      {/* Sent */}
      <button
        type="button"
        onClick={() => onClickStatus(activeStatus === 'sent' ? null : 'sent')}
        className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
          ${activeStatus === 'sent'
            ? 'border-plum bg-plum/5 shadow-brand-sm'
            : 'border-stone bg-white hover:border-plum/30'
          }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
          ${activeStatus === 'sent' ? 'bg-plum/15' : 'bg-stone/60'}`}>
          <FileText size={14} className={activeStatus === 'sent' ? 'text-plum' : 'text-slate'} />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{sentCount}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">Sent</p>
        </div>
      </button>

      {/* Responded — only shown if > 0 */}
      {respondedCount > 0 && (
        <button
          type="button"
          onClick={() => onClickStatus(activeStatus === 'response_received' ? null : 'response_received')}
          className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
            ${activeStatus === 'response_received'
              ? 'border-sky-400 bg-sky-50 shadow-brand-sm'
              : 'border-sky-200 bg-sky-50/50 hover:border-sky-300'
            }`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${activeStatus === 'response_received' ? 'bg-sky-100' : 'bg-sky-100/60'}`}>
            <FileText size={14} className="text-sky-600" />
          </div>
          <div className="min-w-0">
            <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{respondedCount}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700 mt-0.5">Responded</p>
          </div>
        </button>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const DEFAULT_FILTERS: FilterState = {
  visibleStatuses: new Set(ALL_STATUSES),
  myQuotesOnly: false,
  typeCommercial: true,
  typeResidential: true,
};

export function QuotesListPage({ embedded, onSidebarContent }: { embedded?: boolean; onSidebarContent?: (content: React.ReactNode) => void } = {}) {
  const navigate = useNavigate();
  const { activeOrg, currentUser, loading: profileLoading, hasActiveSubscription } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const noSubscription = !hasActiveSubscription && !currentUser?.isPlatformAdmin;
  const uid = currentUser?.uid ?? undefined;

  // Feature flag checks
  const commercialEnabled = activeOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = activeOrg?.featureResidentialMovingQuotes ?? false;
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  const [showNewSheet, setShowNewSheet] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<QuoteSortField>('estimateVisitDate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // Status filter from dashboard tap (null = show all statuses from filter sheet)
  const [dashStatusFilter, setDashStatusFilter] = useState<QuoteStatus | null>(null);

  // Debounced search for backend queries
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Auto-fallback state: track if we tried "my quotes" and found zero


  // Compute type filter for API: if both or neither selected, don't filter
  const typeFilterForApi = useMemo(() => {
    if (filters.typeCommercial && !filters.typeResidential) return 'commercial' as QuoteType;
    if (!filters.typeCommercial && filters.typeResidential) return 'residential' as QuoteType;
    return undefined;
  }, [filters.typeCommercial, filters.typeResidential]);

  const { quotes, loading, loadingMore, error, hasMore, stats, createMove, fetchMore, refetch } = useQuotes(orgId, {
    search: debouncedSearch || undefined,
    sortBy,
    sortDir,
    leadUid: filters.myQuotesOnly ? uid : undefined,
    type: typeFilterForApi,
    lifecycleStage: 'quote',
  });


  // Apply client-side status + type filtering
  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      // Dashboard status filter takes priority when active
      if (dashStatusFilter) {
        if (quote.quoteStatus !== dashStatusFilter) return false;
      } else {
        if (!filters.visibleStatuses.has(quote.quoteStatus)) return false;
      }
      // Type filter (client-side for when both types enabled)
      if (bothTypesEnabled) {
        const isCommercial = quote.type !== 'residential';
        if (isCommercial && !filters.typeCommercial) return false;
        if (!isCommercial && !filters.typeResidential) return false;
      }
      return true;
    });
  }, [quotes, filters.visibleStatuses, filters.typeCommercial, filters.typeResidential, dashStatusFilter, bothTypesEnabled]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.myQuotesOnly) n++;
    if (filters.visibleStatuses.size < ALL_STATUSES.length) n++;
    if (bothTypesEnabled && (!filters.typeCommercial || !filters.typeResidential)) n++;
    return n;
  }, [filters, bothTypesEnabled]);

  const isDefaultSort = sortBy === 'estimateVisitDate' && sortDir === 'asc';

  // Assign estimator
  const [estimatorQuote, setEstimatorQuote] = useState<Quote | null>(null);
  const handleAssignEstimator = useCallback(async (userId: string) => {
    if (!estimatorQuote || !orgId) return;
    await api.patch(`/quotes/${orgId}/${estimatorQuote.id}`, { quoteLeadUserId: userId });
    setEstimatorQuote(null);
    refetch();
  }, [estimatorQuote, orgId, refetch]);

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, fetchMore]);

  async function handleCreate(data: NewMoveFormData) {
    const quote = await createMove(data as unknown as Record<string, unknown>);
    setShowNewSheet(false);
    navigate(`/moves/${quote.id}`);
    return quote;
  }

  function handleDashStatusClick(status: QuoteStatus | null) {
    setDashStatusFilter(status);
  }

  function handleSortApply(field: QuoteSortField, dir: SortDir) {
    setSortBy(field);
    setSortDir(dir);
  }

  const handleFilterApply = useCallback((f: FilterState) => {
    setFilters(f);
    // When user changes "My quotes" filter, reset the auto-fallback tracking
    // so it doesn't re-trigger on subsequent loads
  }, []);

  if (profileLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Provide sidebar content for desktop two-column layout
  useEffect(() => {
    if (loading || error) { onSidebarContent?.(null); return; }
    onSidebarContent?.(
      <StatsDashboard
        draftCount={stats.draft}
        sentCount={stats.sent}
        respondedCount={stats.response_received}
        activeStatus={dashStatusFilter}
        onClickStatus={handleDashStatusClick}
        vertical
      />,
    );
  }, [onSidebarContent, loading, error, stats, dashStatusFilter, handleDashStatusClick]);

  return (
    <div className={embedded ? '' : 'px-4 pt-6 pb-28 md:px-8 md:max-w-5xl md:mx-auto'}>
      {!embedded && <h1 className="font-display text-h1 text-midnight mb-4">Quotes</h1>}

      {/* Stats dashboard — hidden when embedded (desktop shows in sidebar) */}
      {!loading && !error && !embedded && (
        <StatsDashboard
          draftCount={stats.draft}
          sentCount={stats.sent}
          respondedCount={stats.response_received}
          activeStatus={dashStatusFilter}
          onClickStatus={handleDashStatusClick}
        />
      )}

      {/* (Type filter is now inside the Filters modal) */}

      {/* Search + Filter + Sort row */}
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-0">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Search size={14} className="text-slate" />
          </div>
          <input
            type="search"
            placeholder="Search company, contact, or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Sort button */}
        <button
          type="button"
          onClick={() => setShowSortSheet(true)}
          className={`relative shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${!isDefaultSort
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'
            }`}
          aria-label="Sort"
        >
          <ArrowUpDown size={16} />
        </button>

        {/* Filter button */}
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`relative shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'
            }`}
          aria-label="Filter"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-plum
                             flex items-center justify-center text-[9px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Dashboard status filter indicator */}
      {dashStatusFilter && (
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md
            text-[10px] font-bold uppercase tracking-wider ${QUOTE_STATUS_CLASSES[dashStatusFilter]}`}>
            {QUOTE_STATUS_LABEL[dashStatusFilter]}
          </span>
          <button
            type="button"
            onClick={() => setDashStatusFilter(null)}
            className="text-[11px] text-slate hover:text-midnight font-medium"
          >
            Clear
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load quotes</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && quotes.length === 0 && !debouncedSearch && (
        <div className="flex flex-col items-center py-20 gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
            <FileText size={28} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No quotes yet</p>
            <p className="text-[13px] text-slate leading-snug">
              Tap the <span className="font-semibold text-plum">+</span> button to create your first quote.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && quotes.length === 0 && debouncedSearch && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-mist flex items-center justify-center">
            <Search size={20} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No results</p>
            <p className="text-[13px] text-slate leading-snug">
              No quotes match &ldquo;{debouncedSearch}&rdquo;
            </p>
          </div>
        </div>
      )}

      {!loading && !error && quotes.length > 0 && filteredQuotes.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-mist flex items-center justify-center">
            <Search size={20} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No results</p>
            <p className="text-[13px] text-slate leading-snug">
              Try adjusting your search or filters.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && filteredQuotes.length > 0 && (
        <>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filteredQuotes.map(q => (
              <li key={q.id}>
                <MoveCard
                  data={q}
                  stage="quote"
                  statusLabel={QUOTE_STATUS_LABEL[q.quoteStatus] ?? 'Draft'}
                  statusClasses={QUOTE_STATUS_CLASSES[q.quoteStatus] ?? 'bg-stone text-slate'}
                  onClick={() => navigate(`/moves/${q.id}`)}
                  onAssignEstimator={() => setEstimatorQuote(q)}
                  disabled={noSubscription}
                />
              </li>
            ))}
          </ul>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />

          {loadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="text-plum animate-spin" />
            </div>
          )}
        </>
      )}

      {/* Floating action button — hidden when no active subscription */}
      {!noSubscription && (
        <button
          type="button"
          onClick={() => setShowNewSheet(true)}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+16px)] right-4
                     z-40 h-14 w-14 rounded-full bg-plum text-white shadow-brand-md
                     flex items-center justify-center
                     hover:bg-rose active:opacity-80
                     transition-all duration-150"
          aria-label="New quote"
        >
          <Plus size={26} />
        </button>
      )}

      {showNewSheet && (
        <NewMoveForm
          onClose={() => setShowNewSheet(false)}
          onCreate={handleCreate}
          showTypeSelector={bothTypesEnabled}
          defaultType={residentialEnabled && !commercialEnabled ? 'residential' : 'commercial'}
          orgId={orgId}
        />
      )}

      {showFilterSheet && (
        <FilterSheet
          filters={filters}
          onApply={handleFilterApply}
          onClose={() => setShowFilterSheet(false)}
          showTypeFilter={bothTypesEnabled}
        />
      )}

      {showSortSheet && (
        <SortSheet
          sortBy={sortBy}
          sortDir={sortDir}
          onApply={handleSortApply}
          onClose={() => setShowSortSheet(false)}
        />
      )}

      {estimatorQuote && orgId && (
        <AssignEstimatorSheet
          orgId={orgId}
          customerName={estimatorQuote.customerCompanyName || estimatorQuote.pointOfContact?.name || 'Quote'}
          onAssign={handleAssignEstimator}
          onClose={() => setEstimatorQuote(null)}
        />
      )}

    </div>
  );
}
